/**
 * Smart Series Grouping Algorithm
 * Raggruppa canali TV per serie e stagione
 */

// Pattern di riconoscimento per serie TV
const SERIES_PATTERNS = [
  // "Nome S01 E01" o "Nome S1E1"
  {
    regex: /^(.+?)\s+S(\d+)\s*E(\d+)(.*)$/i,
    extract: (match) => ({
      seriesName: match[1].trim(),
      season: parseInt(match[2]),
      episode: parseInt(match[3]),
      suffix: match[4] || ''
    })
  },
  // "Nome 1x01"
  {
    regex: /^(.+?)\s+(\d+)x(\d+)(.*)$/i,
    extract: (match) => ({
      seriesName: match[1].trim(),
      season: parseInt(match[2]),
      episode: parseInt(match[3]),
      suffix: match[4] || ''
    })
  },
  // "Nome Season 1 Episode 1"
  {
    regex: /^(.+?)\s+Season\s*(\d+).*Episode\s*(\d+)(.*)$/i,
    extract: (match) => ({
      seriesName: match[1].trim(),
      season: parseInt(match[2]),
      episode: parseInt(match[3]),
      suffix: match[4] || ''
    })
  },
  // "Nome Stagione 1 Episodio 1"
  {
    regex: /^(.+?)\s+Stagione\s*(\d+).*Episodio\s*(\d+)(.*)$/i,
    extract: (match) => ({
      seriesName: match[1].trim(),
      season: parseInt(match[2]),
      episode: parseInt(match[3]),
      suffix: match[4] || ''
    })
  }
];

/**
 * Estrae informazioni serie da nome canale
 */
export function extractSeriesInfo(channelName) {
  for (const pattern of SERIES_PATTERNS) {
    const match = channelName.match(pattern.regex);
    if (match) {
      return pattern.extract(match);
    }
  }
  return null;
}

/**
 * Raggruppa canali per serie e stagione
 */
export function groupChannelsBySeries(channels) {
  const seriesMap = new Map();
  const standaloneChannels = [];
  
  channels.forEach(channel => {
    const seriesInfo = extractSeriesInfo(channel.name);
    
    if (seriesInfo) {
      const { seriesName, season, episode, suffix } = seriesInfo;
      const seriesKey = seriesName.toLowerCase();
      const seasonKey = `${seriesKey}_s${season}`;
      
      if (!seriesMap.has(seasonKey)) {
        seriesMap.set(seasonKey, {
          type: 'series',
          seriesName: seriesName,
          season: season,
          episodes: [],
          group: channel.group,
          // Usa il primo episodio per metadati base
          thumbnail: channel.tvgLogo,
          originalLogoUrl: channel.originalLogoUrl
        });
      }
      
      const seriesGroup = seriesMap.get(seasonKey);
      seriesGroup.episodes.push({
        ...channel,
        episode: episode,
        suffix: suffix,
        originalName: channel.name
      });
      
      // Aggiorna thumbnail se questo episodio ha un logo migliore
      if (channel.tvgLogo && !seriesGroup.thumbnail) {
        seriesGroup.thumbnail = channel.tvgLogo;
        seriesGroup.originalLogoUrl = channel.originalLogoUrl;
      }
    } else {
      // Canale standalone (non è una serie)
      standaloneChannels.push({
        ...channel,
        type: 'channel'
      });
    }
  });
  
  // Ordina episodi per numero episodio
  seriesMap.forEach(series => {
    series.episodes.sort((a, b) => a.episode - b.episode);
  });
  
  // Combina serie e canali standalone
  const result = [...Array.from(seriesMap.values()), ...standaloneChannels];
  
  return result;
}

/**
 * Filtra risultati raggruppati (per ricerca)
 */
export function filterGroupedChannels(groupedChannels, searchTerm, selectedGroup) {
  if (!searchTerm && !selectedGroup) {
    return groupedChannels;
  }
  
  const term = searchTerm.toLowerCase();
  
  return groupedChannels.filter(item => {
    // Filtro per gruppo
    if (selectedGroup && item.group !== selectedGroup) {
      return false;
    }
    
    // Se non c'è ricerca testuale, passa il filtro gruppo
    if (!searchTerm) {
      return true;
    }
    
    if (item.type === 'series') {
      // Per le serie, cerca nel nome serie o negli episodi
      const seriesMatch = item.seriesName.toLowerCase().includes(term);
      const episodeMatch = item.episodes.some(ep => 
        ep.originalName.toLowerCase().includes(term)
      );
      return seriesMatch || episodeMatch;
    } else {
      // Per canali standalone, cerca nel nome
      return item.name.toLowerCase().includes(term);
    }
  });
}

/**
 * Conta il totale di canali (inclusi episodi nelle serie)
 */
export function getTotalChannelsCount(groupedChannels) {
  return groupedChannels.reduce((total, item) => {
    if (item.type === 'series') {
      return total + item.episodes.length;
    }
    return total + 1;
  }, 0);
}
